import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { useAuth } from '../contexts/AuthContext';
import type { AccountRole } from '../types';

export function useAccountRole(accountId: string | undefined) {
  const { user, membership } = useAuth();
  const { apiGet } = useApi();
  const [role, setRole] = useState<AccountRole>(null);
  const [loading, setLoading] = useState(true);

  const loadRole = useCallback(async () => {
    if (!accountId || !user) {
      setRole(null);
      setLoading(false);
      return;
    }

    if (membership?.role === 'admin') {
      setRole('admin');
      setLoading(false);
      return;
    }

    // Both the leader/executive and member branches previously read the caller's
    // own `account_team_members.role` row; that read is now served by the
    // org-member-gated GET /v1/account/{id}/role (returns the team role or null).
    try {
      const { role: teamRole } = await apiGet<{ role: string | null }>(
        `/v1/account/${accountId}/role`,
      );

      if (membership?.role === 'leader' || membership?.role === 'executive') {
        // Leaders/executives get read-only access whenever a team row exists.
        setRole(teamRole ? 'viewer' : null);
      } else {
        setRole((teamRole as AccountRole) ?? null);
      }
    } catch {
      // Matches the legacy `if (error) setRole(null)` / empty-result behaviour.
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, user, membership, apiGet]);

  useEffect(() => { loadRole(); }, [loadRole]);

  const isOwner = role === 'owner' || role === 'admin';
  const canEdit = isOwner;
  const canExecute = role === 'owner' || role === 'member' || role === 'admin';

  return { role, isOwner, canEdit, canExecute, loading, refresh: loadRole };
}
